<template>
  <div class="p-6 h-full flex flex-col">
    <div class="mb-4">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-1">Categorías</h1>
      <p class="text-gray-500 dark:text-gray-400 text-sm">Organiza los productos del POS en categorías y subcategorías</p>
    </div>

    <!-- Panel dividido -->
    <div class="flex gap-4 flex-1 min-h-0">

      <!-- Panel izquierdo: lista de raíces -->
      <div class="w-72 flex-shrink-0 flex flex-col bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <!-- Header izquierdo -->
        <div class="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
          <span class="font-semibold text-gray-800 dark:text-white text-sm">Todas las categorías</span>
          <button
            @click="abrirModalNueva(null)"
            class="flex items-center gap-1 px-2 py-1 text-xs bg-violet-600 text-white rounded hover:bg-violet-700 transition font-semibold"
          >
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
            Nueva
          </button>
        </div>

        <!-- Búsqueda -->
        <div class="p-2 border-b border-gray-100 dark:border-gray-700">
          <input
            v-model="buscarRaiz"
            @input="cargarRaices(1)"
            type="text"
            placeholder="Buscar categoría..."
            class="w-full px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-400"
          />
        </div>

        <!-- Lista raíces -->
        <div class="flex-1 overflow-y-auto">
          <div v-if="loadingRaices" class="p-4 text-center text-gray-400 text-sm">Cargando...</div>
          <template v-else>
            <div
              v-for="cat in raices"
              :key="cat.id"
              @click="seleccionarCategoria(cat)"
              class="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-violet-50 dark:hover:bg-gray-700 transition group"
              :class="categoriaSeleccionada?.id === cat.id ? 'bg-violet-100 dark:bg-violet-900/30' : ''"
            >
              <span class="text-sm flex-shrink-0">📁</span>

              <!-- Nombre -->
              <div class="flex-1 min-w-0">
                <span class="text-sm font-medium text-gray-800 dark:text-gray-200 truncate block">{{ cat.nombre }}</span>
                <span v-if="cat.parent_id" class="text-xs text-gray-400 truncate block">subcategoría</span>
              </div>

              <!-- Badges -->
              <div class="flex gap-1 flex-shrink-0">
                <span v-if="cat.hijos_count" class="text-xs bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-1.5 py-0.5 rounded-full font-semibold">{{ cat.hijos_count }}</span>
                <span v-if="cat.productos_count" class="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-1.5 py-0.5 rounded-full font-semibold">{{ cat.productos_count }}</span>
              </div>

              <!-- Botones hover -->
              <div class="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" @click.stop>
                <button @click="abrirModalEditar(cat)" class="p-0.5 text-blue-500 hover:text-blue-700" title="Editar">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                </button>
                <button @click="eliminarCat(cat)" class="p-0.5 text-red-500 hover:text-red-700" title="Eliminar">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                </button>
              </div>
            </div>
            <div v-if="raices.length === 0" class="p-4 text-center text-gray-400 text-sm">Sin categorías raíz</div>
          </template>
        </div>

        <!-- Paginación izquierda -->
        <div v-if="paginacionRaices.last_page > 1" class="p-2 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <button @click="cargarRaices(paginacionRaices.current_page - 1)" :disabled="paginacionRaices.current_page <= 1" class="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 disabled:opacity-40">Ant.</button>
          <span class="text-xs text-gray-500">{{ paginacionRaices.current_page }}/{{ paginacionRaices.last_page }}</span>
          <button @click="cargarRaices(paginacionRaices.current_page + 1)" :disabled="paginacionRaices.current_page >= paginacionRaices.last_page" class="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 disabled:opacity-40">Sig.</button>
        </div>
      </div>

      <!-- Panel derecho: árbol -->
      <div class="flex-1 flex flex-col bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 min-w-0">

        <!-- Estado vacío -->
        <div v-if="!categoriaSeleccionada" class="flex-1 flex items-center justify-center">
          <div class="text-center text-gray-400 dark:text-gray-500">
            <svg class="w-16 h-16 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
            </svg>
            <p class="text-sm font-medium">Selecciona una categoría</p>
            <p class="text-xs mt-1">para ver su árbol jerárquico</p>
          </div>
        </div>

        <template v-else>
          <!-- Header derecho -->
          <div class="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
            <span class="font-semibold text-gray-800 dark:text-white text-sm truncate">📁 {{ categoriaSeleccionada.nombre }}</span>
            <div class="flex gap-2 flex-shrink-0">
              <button
                @click="abrirModalNueva(categoriaSeleccionada)"
                class="flex items-center gap-1 px-2 py-1 text-xs bg-violet-600 text-white rounded hover:bg-violet-700 transition font-semibold"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
                Subcategoría
              </button>
              <button
                @click="abrirModalProductos(categoriaSeleccionada)"
                class="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition font-semibold"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
                + Producto
              </button>
            </div>
          </div>

          <!-- Árbol -->
          <div class="flex-1 overflow-y-auto p-1">
            <div v-if="loadingArbol" class="p-4 text-center text-gray-400 text-sm">Cargando árbol...</div>
            <div v-else-if="errorArbol" class="p-4 text-center text-red-400 text-sm">{{ errorArbol }}</div>
            <ul v-else-if="arbol" class="space-y-0.5">
              <NodoCategoria
                :nodo="arbol"
                :nivel="0"
                @refresh="recargarArbol"
                @agregarSub="abrirModalNueva"
                @agregarProd="abrirModalProductos"
                @quitarProd="quitarProducto"
                @editar="abrirModalEditar"
              />
            </ul>
            <div v-else class="p-4 text-center text-gray-400 text-sm">Sin datos</div>
          </div>
        </template>
      </div>
    </div>

    <!-- ===== MODAL: Nueva Subcategoría / Categoría Raíz ===== -->
    <div v-if="showModalNueva" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md shadow-xl">
        <div class="flex justify-between items-center px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 class="font-bold text-gray-900 dark:text-white">Nueva Categoría</h3>
          <button @click="showModalNueva = false" class="text-gray-400 hover:text-gray-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
        <form @submit.prevent="guardarNuevaCategoria" class="px-5 py-4 space-y-4">
          <div>
            <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Nombre <span class="text-red-500">*</span></label>
            <input
              v-model="formNueva.nombre"
              type="text"
              maxlength="100"
              required
              autofocus
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              placeholder="Nombre de la categoría"
            />
            <p v-if="erroresNueva.nombre" class="text-red-500 text-xs mt-1">{{ erroresNueva.nombre[0] }}</p>
          </div>
          <div>
            <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Descripción</label>
            <textarea
              v-model="formNueva.descripcion"
              maxlength="255"
              rows="2"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm resize-none focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              placeholder="Descripción opcional..."
            ></textarea>
          </div>
          <!-- Selector de padre -->
          <div>
            <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Categoría padre</label>
            <div class="relative">
              <input
                v-model="buscarPadreNueva"
                @focus="mostrarDropdownNueva = true"
                @input="filtrarCatsPadreNueva"
                type="text"
                :placeholder="formNueva.parent_id ? catPadreSeleccionadaNueva : '— Sin padre (categoría raíz) —'"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              />
              <button
                v-if="formNueva.parent_id"
                type="button"
                @click="limpiarPadreNueva"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
              <div
                v-if="mostrarDropdownNueva && catsPadreNueva.length"
                class="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-y-auto"
              >
                <button
                  v-for="cat in catsPadreNueva"
                  :key="cat.id"
                  type="button"
                  @click="elegirPadreNueva(cat)"
                  class="w-full text-left px-3 py-2 text-sm hover:bg-violet-50 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200"
                >
                  {{ cat.nombre }}
                  <span v-if="cat.parent_id" class="text-xs text-gray-400 ml-1">(subcategoría)</span>
                </button>
              </div>
            </div>
          </div>
          <div class="flex justify-end gap-3 pt-2">
            <button type="button" @click="showModalNueva = false" class="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600">Cancelar</button>
            <button type="submit" :disabled="guardandoNueva" class="px-5 py-2 text-sm font-medium text-white bg-violet-600 rounded-md hover:bg-violet-700 disabled:opacity-50">
              {{ guardandoNueva ? 'Guardando...' : 'Crear' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- ===== MODAL: Editar Categoría ===== -->
    <div v-if="showModalEditar" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md shadow-xl">
        <div class="flex justify-between items-center px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 class="font-bold text-gray-900 dark:text-white">Editar Categoría</h3>
          <button @click="showModalEditar = false" class="text-gray-400 hover:text-gray-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
        <form @submit.prevent="guardarEdicion" class="px-5 py-4 space-y-4">
          <div>
            <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Nombre <span class="text-red-500">*</span></label>
            <input
              v-model="formEditar.nombre"
              type="text"
              maxlength="100"
              required
              autofocus
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
            />
            <p v-if="erroresEditar.nombre" class="text-red-500 text-xs mt-1">{{ erroresEditar.nombre[0] }}</p>
          </div>
          <div>
            <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Descripción</label>
            <textarea
              v-model="formEditar.descripcion"
              maxlength="255"
              rows="2"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm resize-none focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              placeholder="Descripción opcional..."
            ></textarea>
          </div>
          <!-- Selector de padre -->
          <div>
            <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Categoría padre</label>
            <div class="relative">
              <input
                v-model="buscarPadreEditar"
                @focus="mostrarDropdownEditar = true"
                @input="filtrarCatsPadreEditar"
                type="text"
                :placeholder="formEditar.parent_id ? catPadreSeleccionadaEditar : '— Sin padre (categoría raíz) —'"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              />
              <button
                v-if="formEditar.parent_id"
                type="button"
                @click="limpiarPadreEditar"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
              <div
                v-if="mostrarDropdownEditar && catsPadreEditar.length"
                class="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-y-auto"
              >
                <button
                  v-for="cat in catsPadreEditar"
                  :key="cat.id"
                  type="button"
                  @click="elegirPadreEditar(cat)"
                  class="w-full text-left px-3 py-2 text-sm hover:bg-violet-50 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200"
                >
                  {{ cat.nombre }}
                  <span v-if="cat.parent_id" class="text-xs text-gray-400 ml-1">(subcategoría)</span>
                </button>
              </div>
            </div>
            <p class="text-xs text-gray-400 mt-1">Cambiar el padre moverá esta categoría en el árbol</p>
          </div>
          <div class="flex justify-end gap-3 pt-2">
            <button type="button" @click="showModalEditar = false" class="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600">Cancelar</button>
            <button type="submit" :disabled="guardandoEditar" class="px-5 py-2 text-sm font-medium text-white bg-violet-600 rounded-md hover:bg-violet-700 disabled:opacity-50">
              {{ guardandoEditar ? 'Guardando...' : 'Guardar' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- ===== MODAL: Agregar Productos ===== -->
    <div v-if="showModalProductos" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg shadow-xl flex flex-col max-h-[80vh]">
        <div class="flex justify-between items-center px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h3 class="font-bold text-gray-900 dark:text-white">Agregar Productos a "{{ modalProdCategoria?.nombre }}"</h3>
          <button @click="showModalProductos = false" class="text-gray-400 hover:text-gray-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <div class="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
          <input
            v-model="buscarProductos"
            @input="cargarProductosModal"
            type="text"
            placeholder="Buscar productos..."
            class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-400"
          />
        </div>

        <div class="flex-1 overflow-y-auto px-5 py-2">
          <div v-if="loadingProductosModal" class="py-4 text-center text-gray-400 text-sm">Cargando...</div>
          <div v-else>
            <label
              v-for="prod in productosModal"
              :key="prod.id"
              class="flex items-center gap-3 py-2 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 px-2 rounded"
            >
              <input type="checkbox" :value="prod.id" v-model="productosSeleccionados" class="accent-violet-600 w-4 h-4 flex-shrink-0" />
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{{ prod.nombre }}</div>
                <div class="text-xs text-gray-400 font-mono">{{ prod.producto_id }}</div>
              </div>
              <span class="text-sm text-gray-500 flex-shrink-0">
                {{ prod.precio != null ? `$${Number(prod.precio).toFixed(2)}` : '—' }}
              </span>
            </label>
            <div v-if="productosModal.length === 0" class="py-4 text-center text-gray-400 text-sm">Sin productos</div>
          </div>
        </div>

        <div class="flex justify-between items-center px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <span class="text-sm text-gray-500">{{ productosSeleccionados.length }} seleccionados</span>
          <div class="flex gap-3">
            <button type="button" @click="showModalProductos = false" class="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200">Cancelar</button>
            <button
              @click="agregarProductosSeleccionados"
              :disabled="productosSeleccionados.length === 0 || guardandoProductos"
              class="px-5 py-2 text-sm font-medium text-white bg-violet-600 rounded-md hover:bg-violet-700 disabled:opacity-50"
            >
              {{ guardandoProductos ? 'Agregando...' : 'Agregar' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useHttp } from '@/composables/useHttp.js'
import NodoCategoria from '@/components/Pos2/NodoCategoria.vue'
import Swal from 'sweetalert2'

// Instancias separadas para evitar que el `data` compartido se pise entre llamadas concurrentes
const httpLista    = useHttp()
const httpArbol    = useHttp()
const httpCats     = useHttp()
const httpMutacion = useHttp()
const httpProductos = useHttp()

// ─── Panel izquierdo ──────────────────────────────────────────────────────────
const raices        = ref([])
const loadingRaices = ref(false)
const buscarRaiz    = ref('')
const paginacionRaices = ref({ current_page: 1, last_page: 1, per_page: 30, total: 0 })
const categoriaSeleccionada = ref(null)

// ─── Panel derecho ────────────────────────────────────────────────────────────
const arbol        = ref(null)
const loadingArbol = ref(false)
const errorArbol   = ref(null)

// ─── Catálogo de categorías para selects (lista plana) ───────────────────────
const todasCats = ref([])

const cargarTodasCats = async (excluirId = null) => {
  const params = new URLSearchParams({ todas: 1 })
  if (excluirId) params.append('excluir', excluirId)
  await httpCats.get(`/pos2/categorias?${params}`)
  todasCats.value = httpCats.data.value
}

// ─── Modal nueva categoría ────────────────────────────────────────────────────
const showModalNueva      = ref(false)
const guardandoNueva      = ref(false)
const erroresNueva        = ref({})
const formNueva           = ref({ nombre: '', descripcion: '', parent_id: null })

const buscarPadreNueva          = ref('')
const catPadreSeleccionadaNueva = ref('')
const mostrarDropdownNueva      = ref(false)
const catsPadreNueva            = ref([])

const filtrarCatsPadreNueva = () => {
  const q = buscarPadreNueva.value.toLowerCase()
  catsPadreNueva.value = todasCats.value.filter(c => c.nombre.toLowerCase().includes(q))
}
const elegirPadreNueva = (cat) => {
  formNueva.value.parent_id = cat.id
  catPadreSeleccionadaNueva.value = cat.nombre
  buscarPadreNueva.value = cat.nombre
  mostrarDropdownNueva.value = false
}
const limpiarPadreNueva = () => {
  formNueva.value.parent_id = null
  catPadreSeleccionadaNueva.value = ''
  buscarPadreNueva.value = ''
  catsPadreNueva.value = todasCats.value
}

// ─── Modal editar categoría ───────────────────────────────────────────────────
const showModalEditar     = ref(false)
const categoriaEditando   = ref(null)
const guardandoEditar     = ref(false)
const erroresEditar       = ref({})
const formEditar          = ref({ nombre: '', descripcion: '', parent_id: null })

const buscarPadreEditar          = ref('')
const catPadreSeleccionadaEditar = ref('')
const mostrarDropdownEditar      = ref(false)
const catsPadreEditar            = ref([])

const filtrarCatsPadreEditar = () => {
  const q = buscarPadreEditar.value.toLowerCase()
  catsPadreEditar.value = todasCats.value.filter(c => c.nombre.toLowerCase().includes(q))
}
const elegirPadreEditar = (cat) => {
  formEditar.value.parent_id = cat.id
  catPadreSeleccionadaEditar.value = cat.nombre
  buscarPadreEditar.value = cat.nombre
  mostrarDropdownEditar.value = false
}
const limpiarPadreEditar = () => {
  formEditar.value.parent_id = null
  catPadreSeleccionadaEditar.value = ''
  buscarPadreEditar.value = ''
  catsPadreEditar.value = todasCats.value
}

// ─── Modal productos ──────────────────────────────────────────────────────────
const showModalProductos     = ref(false)
const modalProdCategoria     = ref(null)
const productosModal         = ref([])
const productosSeleccionados = ref([])
const buscarProductos        = ref('')
const loadingProductosModal  = ref(false)
const guardandoProductos     = ref(false)

// ─── Carga lista izquierda (todas las categorías, paginadas) ──────────────────
const cargarRaices = async (pagina = 1) => {
  try {
    loadingRaices.value = true
    const params = new URLSearchParams({ todas: 1 })
    if (buscarRaiz.value) params.append('buscar', buscarRaiz.value)
    await httpLista.get(`/pos2/categorias?${params}`)
    // todas=1 devuelve array plano, no paginado — paginamos en cliente
    const todas = httpLista.data.value ?? []
    const perPage = paginacionRaices.value.per_page
    const total   = todas.length
    const lastPage = Math.max(1, Math.ceil(total / perPage))
    const page = Math.min(pagina, lastPage)
    const offset = (page - 1) * perPage
    raices.value = todas.slice(offset, offset + perPage)
    paginacionRaices.value = { current_page: page, last_page: lastPage, per_page: perPage, total }
  } finally {
    loadingRaices.value = false
  }
}

// ─── Seleccionar categoría → cargar árbol ────────────────────────────────────
const seleccionarCategoria = async (cat) => {
  // Si es subcategoría, navegar al árbol de la raíz correspondiente
  // Para simplificar, cargamos el árbol de la categoría seleccionada directamente
  categoriaSeleccionada.value = cat
  await cargarArbol(cat.id)
}

const cargarArbol = async (id) => {
  loadingArbol.value = true
  errorArbol.value = null
  arbol.value = null
  try {
    await httpArbol.get(`/pos2/categorias/${id}/arbol`)
    arbol.value = httpArbol.data.value
  } catch (err) {
    errorArbol.value = err.response?.data?.message ?? 'Error al cargar el árbol'
  } finally {
    loadingArbol.value = false
  }
}

const recargarArbol = async () => {
  await cargarRaices(paginacionRaices.value.current_page)
  if (categoriaSeleccionada.value) {
    await cargarArbol(categoriaSeleccionada.value.id)
  }
}

const eliminarCat = async (cat) => {
  const result = await Swal.fire({
    title: '¿Estás seguro?',
    text: `¿Eliminar "${cat.nombre}"?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar',
  })
  if (result.isConfirmed) {
    try {
      await httpMutacion.del(`/pos2/categorias/${cat.id}`)
      if (categoriaSeleccionada.value?.id === cat.id) {
        categoriaSeleccionada.value = null
        arbol.value = null
      }
      cargarRaices(paginacionRaices.value.current_page)
    } catch {
      Swal.fire('Error', 'No se pudo eliminar', 'error')
    }
  }
}

// ─── Modal nueva categoría ────────────────────────────────────────────────────
const abrirModalNueva = async (parent) => {
  await cargarTodasCats()
  formNueva.value = { nombre: '', descripcion: '', parent_id: parent?.id ?? null }
  buscarPadreNueva.value = parent?.nombre ?? ''
  catPadreSeleccionadaNueva.value = parent?.nombre ?? ''
  catsPadreNueva.value = todasCats.value
  mostrarDropdownNueva.value = false
  erroresNueva.value = {}
  showModalNueva.value = true
}

const guardarNuevaCategoria = async () => {
  try {
    guardandoNueva.value = true
    erroresNueva.value = {}
    await httpMutacion.post('/pos2/categorias', {
      nombre:      formNueva.value.nombre,
      descripcion: formNueva.value.descripcion || null,
      parent_id:   formNueva.value.parent_id ?? null,
    })
    showModalNueva.value = false
    await recargarArbol()
  } catch (err) {
    erroresNueva.value = err.response?.data?.errors ?? {}
    if (!Object.keys(erroresNueva.value).length) {
      Swal.fire('Error', err.response?.data?.message ?? 'Error al crear', 'error')
    }
  } finally {
    guardandoNueva.value = false
  }
}

// ─── Modal editar categoría ───────────────────────────────────────────────────
const abrirModalEditar = async (cat) => {
  await cargarTodasCats(cat.id)
  categoriaEditando.value = cat
  formEditar.value = {
    nombre:      cat.nombre,
    descripcion: cat.descripcion ?? '',
    parent_id:   cat.parent_id ?? null,
  }
  const padreActual = cat.parent_id ? todasCats.value.find(c => c.id === cat.parent_id) : null
  buscarPadreEditar.value = padreActual?.nombre ?? ''
  catPadreSeleccionadaEditar.value = padreActual?.nombre ?? ''
  catsPadreEditar.value = todasCats.value
  mostrarDropdownEditar.value = false
  erroresEditar.value = {}
  showModalEditar.value = true
}

const guardarEdicion = async () => {
  try {
    guardandoEditar.value = true
    erroresEditar.value = {}
    await httpMutacion.put(`/pos2/categorias/${categoriaEditando.value.id}`, {
      nombre:      formEditar.value.nombre,
      descripcion: formEditar.value.descripcion || null,
      parent_id:   formEditar.value.parent_id ?? null,
    })
    showModalEditar.value = false
    if (categoriaSeleccionada.value?.id === categoriaEditando.value.id) {
      categoriaSeleccionada.value.nombre = formEditar.value.nombre
    }
    await recargarArbol()
  } catch (err) {
    erroresEditar.value = err.response?.data?.errors ?? {}
    if (!Object.keys(erroresEditar.value).length) {
      Swal.fire('Error', err.response?.data?.message ?? 'Error al guardar', 'error')
    }
  } finally {
    guardandoEditar.value = false
  }
}

// ─── Modal productos ──────────────────────────────────────────────────────────
const abrirModalProductos = async (cat) => {
  modalProdCategoria.value = cat
  productosSeleccionados.value = []
  buscarProductos.value = ''
  showModalProductos.value = true
  await cargarProductosModal()
}

const cargarProductosModal = async () => {
  try {
    loadingProductosModal.value = true
    const params = new URLSearchParams({ per_page: 50, activo: 1 })
    if (buscarProductos.value) params.append('buscar', buscarProductos.value)
    await httpProductos.get(`/pos2/productos?${params}`)
    productosModal.value = httpProductos.data.value.data
  } finally {
    loadingProductosModal.value = false
  }
}

const agregarProductosSeleccionados = async () => {
  if (!productosSeleccionados.value.length) return
  try {
    guardandoProductos.value = true
    await httpMutacion.post(`/pos2/categorias/${modalProdCategoria.value.id}/productos`, {
      producto_ids: productosSeleccionados.value,
    })
    showModalProductos.value = false
    await recargarArbol()
  } catch {
    Swal.fire('Error', 'No se pudieron agregar los productos', 'error')
  } finally {
    guardandoProductos.value = false
  }
}

// ─── Quitar producto ──────────────────────────────────────────────────────────
const quitarProducto = async ({ categoria, producto }) => {
  const result = await Swal.fire({
    title: '¿Quitar producto?',
    text: `¿Quitar "${producto.nombre}" de la categoría "${categoria.nombre}"?`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Sí, quitar',
    cancelButtonText: 'Cancelar',
  })
  if (result.isConfirmed) {
    try {
      await httpMutacion.del(`/pos2/categorias/${categoria.id}/productos/${producto.id}`)
      await recargarArbol()
    } catch {
      Swal.fire('Error', 'No se pudo quitar el producto', 'error')
    }
  }
}

// ─── Init ─────────────────────────────────────────────────────────────────────
cargarRaices()
</script>
